<script setup lang="ts">
import InputAmount from "@components/ui/input-amount/InputAmount.vue";
import DefaultAmountButton from "@components/ui/default-amount-button/DefaultAmountButton.vue";
import CreatePaymentViewController from "@/targets/light/views/create-payment/ts/CreatePaymentViewController";
import {useI18n} from "vue-i18n";

const viewController = CreatePaymentViewController.of();

const {
	inputAmount,
	suggestedAmounts,
	selectedPaymentFlow,
	currency,
	minMaxRangesBySelectedFlow,
	isInputAmountError,
	isPaymentFlowError,
	isInputAmountDirty,
    isAgentBetweenRangeError,
    agentBetweenRanges,
    paymentService,
} = viewController;

const { t } = useI18n();

const handleClickDefaultAmount = (value: number) => {
	inputAmount.value = value;
	handleChangeInputAmount(value);
}

const handleClickMaxDefaultAmount = () => {
	if (minMaxRangesBySelectedFlow.value && selectedPaymentFlow.value) {
		inputAmount.value = minMaxRangesBySelectedFlow.value.max;
	}
}

const handleBlurInputAmount = () => {
	isInputAmountDirty.value = true;
}

const isInRanges = (value: number) => {
	if (agentBetweenRanges.value) {
		return agentBetweenRanges.value.some(({ min, max }) => value >= min && value <= max);
    }
}

const handleChangeInputAmount = (value: number) => {
	if (isAgentBetweenRangeError.value && isInRanges(value)) {
        paymentService.paymentError = null;
    }
}
</script>

<template>
	<div class="create-payment-input">
		<InputAmount
			class="create-payment-input__input"
			:header-title="t('light-payment.create.input.title')"
			v-model="inputAmount"
			:currency="currency"
            @update:modelValue="handleChangeInputAmount"
			@blur="handleBlurInputAmount"
		>
			<template v-if="isInputAmountError || isPaymentFlowError" #hint>
				<p class="create-payment-input__input-hint-error">
					{{ t('light-payment.create.input.errors.unavailable-amount') }}
				</p>
			</template>
		</InputAmount>
		<div class="create-payment-input__amounts">
			<DefaultAmountButton
				v-for="amount in suggestedAmounts"
				:key="amount"
				:amount="amount"
				:currency="currency"
				@click="handleClickDefaultAmount(amount)"
			/>
			<DefaultAmountButton
				:disabled="!selectedPaymentFlow"
				amount="MAX"
				:currency="currency"
				@click="handleClickMaxDefaultAmount"
			/>
		</div>
	</div>
</template>

<style scoped lang="scss">
.create-payment-input {
	&__input {
		margin-top: unset;
		margin-bottom: 0.75rem;

		&:deep(.input-amount__header) {
			justify-content: center;
			margin-bottom: 0.25rem;
		}

		&:deep(.input-amount__header-title) {
			text-transform: none;
			text-align: center;

			@include text(14px, 500, var(--grey2), 120%);
		}

		&:deep(.input-amount__input input) {
			text-align: center;
			height: 100%;

			@include text(44px, 700, var(--brown1), 120%);
		}

		&:deep(ul) {
			&::before {
				content: none;
			}

			&::after {
				background-color: #DBDDE4;
			}

			.item-input {
				padding-left: 1rem;
			}

			.item-inner {
				padding-top: 0.25rem;
				padding-bottom: 0.25rem;
				padding-right: 1rem;
				height: 3.875rem;
			}
		}

		&:deep(.input-amount__hint) {
			display: flex;
			justify-content: center;
		}
	}

	&__input-hint-error {
		color: var(--red);
	}

	&__amounts {
		display: flex;
		justify-content: center;
		align-items: center;
		flex-wrap: wrap;
		gap: 0.5rem;
	}
}
</style>
